/**
 * Site visuals media is served from nginx at /site-visuals/.
 *
 * Survey-backed tabs (Walkthrough / Orthomosaic / 3D) prefer DB `site_images`
 * paths via the surveys API. This module only supplies conventional fallbacks
 * when the API has no row yet (e.g. project cards):
 *
 *   site-visuals/{Project.code}/orthomosaic.jpg
 *   site-visuals/{Project.code}/walkthrough_hls/index.m3u8
 *   site-visuals/{Project.code}/model.glb
 *   …
 */

export const SITE_VISUALS_BASE = "/site-visuals";

export function siteVisualsUrl(folder, filename) {
  if (!filename) return null;
  const encodePath = (value) =>
    value.split("/").filter(Boolean).map(encodeURIComponent).join("/");
  if (!folder) return `${SITE_VISUALS_BASE}/${encodePath(filename)}`;
  return `${SITE_VISUALS_BASE}/${encodePath(folder)}/${encodePath(filename)}`;
}

/** Conventional filenames under site-visuals/{code}/ */
export const DEFAULT_MEDIA_FILES = {
  walkthrough: "walkthrough.mp4",
  walkthroughStream: "walkthrough_hls/index.m3u8",
  walkthroughMoments: "walkthrough_hls/moments.json",
  orthomosaic: "orthomosaic.jpg",
  orthomosaicPreview: "orthomosaic.preview.jpg",
  orthomosaicTiles: "orthomosaic.dzi",
  orthomosaicCard: "orthomosaic.card.jpg",
  terrain: "terrain.jpg",
  pointCloud: "pointcloud.ply",
  pointCloudTiles: "pointcloud_potree/metadata.json",
  modelMesh: "model.glb",
};

function projectMedia(folder, files = {}) {
  return {
    folder: folder || "",
    card: siteVisualsUrl(folder, files.orthomosaicCard),
    walkthrough: siteVisualsUrl(folder, files.walkthroughStream || files.walkthrough),
    walkthroughOriginal: siteVisualsUrl(folder, files.walkthrough),
    walkthroughMoments: siteVisualsUrl(folder, files.walkthroughMoments),
    orthomosaic: siteVisualsUrl(folder, files.orthomosaicPreview || files.orthomosaic),
    orthomosaicOriginal: siteVisualsUrl(folder, files.orthomosaic),
    orthomosaicTiles: siteVisualsUrl(folder, files.orthomosaicTiles),
    terrain: siteVisualsUrl(folder, files.terrainPreview || files.terrain),
    surface: siteVisualsUrl(folder, files.surfacePreview || files.surface),
    pointCloud: siteVisualsUrl(folder, files.pointCloud),
    pointCloudTiles: siteVisualsUrl(folder, files.pointCloudTiles),
    modelMesh: siteVisualsUrl(folder, files.modelMesh),
  };
}

function emptyMedia() {
  return projectMedia("", {});
}

function normalizeCode(code) {
  return String(code || "").trim();
}

/**
 * Resolve media URLs for a project using its DB `code`.
 * Convention only — Site Visuals tabs should override from `site_images`.
 * @param {string} code - projects.code
 */
export function resolveProjectMedia(code) {
  const normalized = normalizeCode(code);
  if (!normalized) return emptyMedia();
  return projectMedia(normalized, DEFAULT_MEDIA_FILES);
}

/**
 * Card thumbnail: prefer API imageUrl, else code-linked orthomosaic card.
 * API often stores a site-visuals-relative path (e.g. EMBASSY-EDEN/F1/….card.jpg)
 * without the /site-visuals/ prefix — normalize before using as <img src>.
 */
export function resolveProjectCardImage(code, imageUrlFromApi) {
  if (imageUrlFromApi) {
    const trimmed = String(imageUrlFromApi).trim();
    if (!trimmed) return resolveProjectMedia(code).card || null;
    if (/^https?:\/\//i.test(trimmed) || trimmed.startsWith("/")) return trimmed;
    return siteVisualsUrl(null, trimmed);
  }
  return resolveProjectMedia(code).card || null;
}

/**
 * Build a minimal survey list from API project fields (until surveys API exists).
 */
export function surveysFromProject(project) {
  if (!project) return [];
  const code = project.code || project.id || "project";
  const label =
    project.lastSurvey ||
    project.lastSurveyAt ||
    project.activeSurvey ||
    "Latest survey";
  return [
    {
      id: `survey-${code}-latest`,
      dateLabel: label,
      shortDate: label,
      isLatest: true,
      drone: "—",
      capturedImages: "—",
      groundResolution: "—",
      areaCovered: "—",
      weather: "—",
      flightDuration: "—",
    },
  ];
}
